import * as React from "react";
import { useState, useEffect } from "react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Card } from "../../components/ui/card";
import StatsChartViewTemplate from "../charts/StatsChartViewTemplate";
import LoadingState from "../states/LoadingState";
import ErrorState from "../states/ErrorState";
import DateRangeSelector from "./DateRangeSelector";

/**
 * A reusable container for displaying statistics in both trend and breakdown views
 *
 * @param {Object} props
 * @param {string} props.id - The ID to use in API queries
 * @param {Function} props.useGetStatsQuery - The RTK Query hook to use for fetching data
 * @param {string} props.title - The title to display
 * @param {boolean} props.showDateFilter - Whether to show the date filter
 */
const StatsContainer = ({
  id,
  useGetStatsQuery,
  title = "Stats Analysis",
  showDateFilter = true,
}) => {
  const [activeView, setActiveView] = useState("trend");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [isManualRefetch, setIsManualRefetch] = useState(false);

  // Prepare query parameters
  const trendQueryParams = {
    glid: id,
    chart_type: "trend",
    ...(startDate && { start_date: startDate }),
    ...(endDate && { end_date: endDate }),
  };

  const breakdownQueryParams = {
    glid: id,
    chart_type: "breakdown",
    ...(startDate && { start_date: startDate }),
    ...(endDate && { end_date: endDate }),
  };

  // Fetch trend data
  const {
    data: trendData,
    isLoading: trendLoading,
    isError: trendError,
    error: trendErrorMessage,
    refetch: refetchTrend,
  } = useGetStatsQuery(trendQueryParams, {
    skip: activeView !== "trend",
    refetchOnMountOrArgChange: true,
  });

  // Fetch breakdown data only when trend data is available and we're on the breakdown tab
  const {
    data: breakdownData,
    isLoading: breakdownLoading,
    isError: breakdownError,
    error: breakdownErrorMessage,
    refetch: refetchBreakdown,
  } = useGetStatsQuery(breakdownQueryParams, {
    skip: activeView !== "breakdown" || !trendData,
    refetchOnMountOrArgChange: true,
  });

  // Handle date range selection from DateRangeSelector
  const handleDateFilterApply = ({
    startDate: newStartDate,
    endDate: newEndDate,
  }) => {
    setIsManualRefetch(true);

    // Update date state
    setStartDate(newStartDate);
    setEndDate(newEndDate);

    // Wait for state to update before refetching
    setTimeout(async () => {
      if (activeView === "trend") {
        await refetchTrend();
      } else if (activeView === "breakdown") {
        await refetchBreakdown();
      }
      setIsManualRefetch(false);
    }, 0);
  };

  // Handle tab change
  const handleTabChange = (newView) => {
    setStartDate(null);
    setEndDate(null);
    setActiveView(newView);
  };

  // Load appropriate data when tab changes
  useEffect(() => {
    if (activeView === "breakdown" && !breakdownData && !breakdownLoading) {
      refetchBreakdown();
    }
  }, [activeView, breakdownData, breakdownLoading, refetchBreakdown]);

  // Check if data is loading
  const isLoading = trendLoading || breakdownLoading || isManualRefetch;

  // Check if there's an error
  const hasError = trendError || breakdownError;
  const errorMessage = (trendErrorMessage || breakdownErrorMessage)?.message;

  if (isLoading) {
    return <LoadingState />;
  }

  if (hasError) {
    return <ErrorState error={errorMessage} />;
  }

  return (
    <Card className="space-y-4">
      <div className="p-3 sm:p-4 lg:p-6">
        <Tabs
          value={activeView}
          onValueChange={handleTabChange}
          className="space-y-4"
        >
          <TabsList className="grid w-full grid-cols-2 h-auto">
            <TabsTrigger
              value="trend"
              className="text-xs sm:text-sm p-2 sm:p-3"
            >
              <span className="hidden sm:inline">Trend Analysis</span>
              <span className="sm:hidden">Trend</span>
            </TabsTrigger>
            <TabsTrigger
              value="breakdown"
              className="text-xs sm:text-sm p-2 sm:p-3"
            >
              <span className="hidden sm:inline">Breakdown Analysis</span>
              <span className="sm:hidden">Breakdown</span>
            </TabsTrigger>
          </TabsList>

          {/* Date Selector - conditionally rendered */}
          {showDateFilter && (
            <div className="mt-3 sm:mt-4">
              <DateRangeSelector
                onApply={handleDateFilterApply}
                loading={isLoading}
              />
            </div>
          )}

          <TabsContent value="trend" className="mt-3 sm:mt-4">
            <StatsChartViewTemplate
              data={trendData}
              chartType="trend"
              title={`${title} - Trend Analysis`}
            />
          </TabsContent>

          <TabsContent value="breakdown" className="mt-3 sm:mt-4">
            <StatsChartViewTemplate
              data={breakdownData}
              chartType="breakdown"
              title={`${title} - Breakdown Analysis`}
            />
          </TabsContent>
        </Tabs>
      </div>
    </Card>
  );
};

export default StatsContainer;
